import { useEffect, useState } from 'react';
import './styles/admin.scss';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { PRODUCT_LABEL } from './adminConstants';

type SalesSource = { source?: string | null; medium?: string | null; campaign?: string | null; visits: number; orders: number; paidOrders: number; revenue: number };
type StatsMonth = { month: string; orders: number; paidOrders: number; revenue: number };
type StatsProduct = { product: number; orders: number; paidOrders: number; revenue: number };
type Stats = {
    generatedAt: string;
    revenueCollected: number;
    revenueOutstanding: number;
    totalOrders: number;
    paidOrders: number;
    averageOrderValue: number;
    activeStudents: number;
    visitors: number;
    pageviews: number;
    months: StatsMonth[];
    products: StatsProduct[];
    sources: SalesSource[];
};

const czk = (n: number) => Math.round(n).toLocaleString("cs-CZ") + " Kč";
const pct = (paid: number, total: number) => (total ? Math.round((paid / total) * 100) : 0);
const srcLabel = (s: SalesSource) => (s.source ? [s.source, s.medium || "—", s.campaign || "—"].join(" / ") : "přímo/organicky");

// Plain-text digest of every number on the page, self-contained so it can be pasted
// straight into an LLM to discuss strategy. Deliberately carries no student personal
// data — only aggregates.
function buildSummary(s: Stats): string {
    const lines: string[] = [];
    lines.push(`MyMed — přehled prodeje (k ${s.generatedAt})`);
    lines.push("MyMed je český online kurz pro přípravu na přijímačky na medicínu (fyzika, chemie, biologie). Prodej přes web, platby přes Stripe, placená reklama běží na Facebooku a Instagramu.");
    lines.push("");
    lines.push("SOUHRN");
    lines.push(`- Vybráno celkem: ${czk(s.revenueCollected)}`);
    lines.push(`- Nezaplacené platby (očekávané): ${czk(s.revenueOutstanding)}`);
    lines.push(`- Objednávky: ${s.totalOrders} (zaplaceno ${s.paidOrders}, konverze ${pct(s.paidOrders, s.totalOrders)} %)`);
    lines.push(`- Průměrná hodnota zaplacené objednávky: ${czk(s.averageOrderValue)}`);
    lines.push(`- Aktivní studenti: ${s.activeStudents}`);
    lines.push("");
    lines.push("NÁVŠTĚVNOST");
    lines.push(`- Unikátní návštěvníci: ${s.visitors}`);
    lines.push(`- Zobrazení stránek: ${s.pageviews}`);
    lines.push(`- Konverze návštěvník → platba: ${pct(s.paidOrders, s.visitors)} %`);
    lines.push("");
    lines.push("VÝVOJ PO MĚSÍCÍCH (objednávky / vybráno)");
    for (const m of s.months) lines.push(`- ${m.month}: ${m.orders} obj. / ${czk(m.revenue)}`);
    lines.push("");
    lines.push("PRODEJ PODLE PRODUKTU (objednávky / zaplaceno / tržby)");
    for (const p of s.products) lines.push(`- ${PRODUCT_LABEL[p.product] ?? p.product}: ${p.orders} / ${p.paidOrders} / ${czk(p.revenue)}`);
    lines.push("");
    lines.push("PRODEJ PODLE ZDROJE (zdroj/médium/kampaň — návštěvy / objednávky / zaplaceno / konverze / tržby)");
    for (const src of s.sources) lines.push(`- ${srcLabel(src)}: ${src.visits} / ${src.orders} / ${src.paidOrders} / ${pct(src.paidOrders, src.orders)} % / ${czk(src.revenue)}`);
    lines.push("");
    lines.push("POZNÁMKY");
    lines.push('- „Konverze" = zaplacené objednávky / všechny objednávky.');
    lines.push('- „přímo/organicky" = návštěvník přišel bez UTM značek (mimo označenou reklamu).');
    lines.push('- „Návštěvy" u zdroje = unikátní návštěvníci (anonymní relace prohlížeče), které daný zdroj přivedl.');
    lines.push("- Tržby = skutečně vybrané peníze (zaplacené platby), ne jen objednané.");
    lines.push("");
    lines.push("Na základě těchto dat mi prosím poraď se strategií — marketing (reklama na FB/IG), ceny a produkty. Zeptej se na doplňující otázky, pokud něco chybí.");
    return lines.join("\n");
}

export default function AdminStats() {
    const [stats, setStats] = useState<Stats | null>(null);
    const [loaded, setLoaded] = useState(false);
    const api = new ApiHelper();

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
    async function load() {
        try { setStats(await (await api.adminStatsApi.summary()).json()); }
        catch (x: any) { api.adminError(x); }
        finally { setLoaded(true); }
    }

    async function copySummary() {
        if (!stats) return;
        try { await navigator.clipboard.writeText(buildSummary(stats)); api.alert.success("Souhrn zkopírován do schránky"); }
        catch { api.alert.error("Kopírování se nezdařilo"); }
    }

    const head = <div className="adminHead"><div><h1>Statistiky</h1><p>Přehled prodeje, konverzí a zdrojů návštěv</p></div></div>;

    if (!loaded) return <div>{head}<p className="muted">Načítání…</p></div>;
    if (!stats) return <div>{head}<p className="muted">Data se nepodařilo načíst.</p></div>;

    const maxRevenue = Math.max(...stats.months.map(m => m.revenue), 1);

    return (
        <div>
            {head}

            <div className="statGrid">
                <div className="statTile"><span className="lbl">Vybráno celkem</span><span className="val">{czk(stats.revenueCollected)}</span><span className="sub">+ {czk(stats.revenueOutstanding)} v nezaplacených platbách</span></div>
                <div className="statTile"><span className="lbl">Objednávky</span><span className="val">{stats.totalOrders}</span><span className="sub">zaplaceno {stats.paidOrders} · konverze {pct(stats.paidOrders, stats.totalOrders)} %</span></div>
                <div className="statTile"><span className="lbl">Ø hodnota objednávky</span><span className="val">{czk(stats.averageOrderValue)}</span><span className="sub">z {stats.paidOrders} zaplacených</span></div>
                <div className="statTile"><span className="lbl">Aktivní studenti</span><span className="val">{stats.activeStudents}</span><span className="sub">s platným přístupem</span></div>
                <div className="statTile"><span className="lbl">Návštěvníci</span><span className="val">{stats.visitors.toLocaleString("cs-CZ")}</span><span className="sub">{stats.pageviews.toLocaleString("cs-CZ")} zobrazení · {pct(stats.paidOrders, stats.visitors)} % koupilo</span></div>
            </div>

            <div className="aCard" style={{ marginTop: "18px" }}>
                <h2>Vývoj po měsících</h2>
                {stats.months.length === 0 ? <p className="muted">Zatím žádná data.</p> :
                    <div className="trendBars">
                        {stats.months.map(m => (
                            <div key={m.month} className="trendRow">
                                <span className="tMonth">{m.month}</span>
                                <span className="tBarWrap"><span className="tBar" style={{ width: (m.revenue / maxRevenue * 100) + "%" }} /></span>
                                <span className="tVal">{czk(m.revenue)}<i>{m.orders} obj.</i></span>
                            </div>
                        ))}
                    </div>
                }
            </div>

            <div className="aGrid" style={{ marginTop: "18px" }}>
                <div className="aCard">
                    <h2>Podle produktu</h2>
                    <table className="aTable">
                        <thead><tr><th>Produkt</th><th>Obj.</th><th>Zapl.</th><th>Tržby</th></tr></thead>
                        <tbody>
                            {stats.products.map(p => (
                                <tr key={p.product}><td>{PRODUCT_LABEL[p.product] ?? p.product}</td><td>{p.orders}</td><td>{p.paidOrders}</td><td>{czk(p.revenue)}</td></tr>
                            ))}
                            {stats.products.length === 0 && <tr><td colSpan={4} className="muted">Zatím žádná data.</td></tr>}
                        </tbody>
                    </table>
                </div>
                <div className="aCard">
                    <h2>Podle zdroje</h2>
                    <table className="aTable">
                        <thead><tr><th>Zdroj / kampaň</th><th>Návš.</th><th>Obj.</th><th>Zapl.</th><th>Konv.</th><th>Tržby</th></tr></thead>
                        <tbody>
                            {stats.sources.map((s, i) => (
                                <tr key={i}><td>{srcLabel(s)}</td><td>{s.visits}</td><td>{s.orders}</td><td>{s.paidOrders}</td><td>{pct(s.paidOrders, s.orders)} %</td><td>{czk(s.revenue)}</td></tr>
                            ))}
                            {stats.sources.length === 0 && <tr><td colSpan={6} className="muted">Zatím žádná data.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="aCard" style={{ marginTop: "18px" }}>
                <div className="cardHeadRow">
                    <h2>Souhrn pro AI</h2>
                    <button className="aBtn" onClick={copySummary}>Kopírovat</button>
                </div>
                <p className="hint" style={{ margin: "0 0 12px 0" }}>
                    Textový souhrn všech čísel výše. Zkopíruj a vlož do ChatGPT/Claude a prober strategii
                    (reklama, ceny, produkty). Neobsahuje osobní údaje studentů.
                </p>
                <textarea className="copyText" readOnly value={buildSummary(stats)} rows={16} onFocus={e => e.currentTarget.select()} />
            </div>
        </div>
    );
}
